import { CommonModule } from "@angular/common"
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  forwardRef,
  HostListener,
  input,
  signal
} from '@angular/core'
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from '@angular/forms'
import {
  getFieldValue,
  groupByFieldDropDown
} from './utils/group-field-dropdown.function'

@Component({
    selector: 'app-group-field-dropdown',
    templateUrl: './group-field-dropdown.component.html',
    styleUrls: ['./group-field-dropdown.component.scss'],
    standalone: true,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => GroupFieldDropdownComponent)
        }
    ],
    imports: [CommonModule, FormsModule, ReactiveFormsModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GroupFieldDropdownComponent<T = any>
  implements ControlValueAccessor
{
  // inputs
  public dataInput = input.required<T[]>()
  public labelKey = input.required<keyof T | ((item: T) => string)>()
  public groupedByField = input.required<keyof T | ((item: T) => string)>()
  public fieldKey = input.required<keyof T | ((item: T) => string)>()
  public placeholder = input<string>('Select an option...')
  public disabled = input<boolean>(false)
  public isInitiativeTypeFieldError = input<boolean>(false)

  // dropdown behavior variables
  protected isOpen = signal(false)
  protected selectedValue = signal<any>(null)

  // computed properties
  protected groupedData = computed(() =>
    groupByFieldDropDown(this.dataInput(), this.groupedByField())
  )

  protected selectedItem = computed(() => {
    const value = this.selectedValue()
    if (!value) return null

    const fieldKey = this.fieldKey()
    const allItems = this.dataInput()

    return (
      allItems.find((item) => {
        const itemValue = fieldKey ? getFieldValue(item, fieldKey) : item
        return itemValue === value
      }) || null
    )
  })

  protected selectedLabel = computed(() => {
    const selectedItem = this.selectedItem()
    if (selectedItem) {
      const labelKey = this.labelKey()
      if (labelKey) {
        return String(getFieldValue(selectedItem, labelKey))
      }
      return String(selectedItem)
    }
    return this.placeholder()
  })

  protected hasData = computed(() => Object.keys(this.groupedData()).length > 0)

  protected isDisabled = computed(() => this._disabled || this.disabled())

  protected _onChange?: (value: any) => void
  protected _onTouched?: () => void
  protected _disabled = false

  constructor(private elementRef: ElementRef) {}

  // ControlValueAccessor implementation
  writeValue(obj: any): void {
    this.selectedValue.set(obj)
  }

  registerOnChange(fn: any): void {
    this._onChange = fn
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled
  }

  // Component methods
  toggleDropdown(): void {
    if (!this.isDisabled()) {
      this.isOpen.update((open) => !open)
      if (this.isOpen()) {
        this._onTouched?.()
      }
    }
  }

  selectItem(item: T): void {
    const fieldKey = this.fieldKey()
    const value = fieldKey ? getFieldValue(item, fieldKey) : item

    this.selectedValue.set(value)
    this.isOpen.set(false)
    this._onChange?.(value)
  }

  // Helper methods for template (but used efficiently)
  getItemLabel(item: T): string {
    const labelKey = this.labelKey()
    if (labelKey) {
      return String(getFieldValue(item, labelKey))
    }
    return String(item)
  }

  isItemSelected(item: T): boolean {
    const fieldKey = this.fieldKey()
    const itemValue = fieldKey ? getFieldValue(item, fieldKey) : item
    return this.selectedValue() === itemValue
  }

  // Helper method to close the dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen.set(false)
    }
  }
}